import * as Yup from "yup";

const noEmojiRegex = /\p{Extended_Pictographic}/u;
const numberRegex = /^[0-9]*$/;
const emailRegex =
  /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/;
const addressRegex =
  /^(?!.*(?:(.*((p|post)[-.\s]*(o|off|office)[-.\s]*(box|bin)[-.\s]*)|.*((p |post)[-.\s]*(box|bin)[-.\s]*)))).*$/i;
const postalcodeRegex = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
const wageCheck = (value: string | undefined) => {
  if (!value) return false;
  const wageRegex = /^([0-9]+([.][0-9]*)?|[.][0-9]+)$/;
  const splitWage = value.split(".");
  if (splitWage[1] && splitWage[1].length > 2) {
    return false;
  }
  return wageRegex.test(value);
};

export interface BaseWCSchemaArgs {
  isOwner?: boolean;
  isCompanyEmployee?: boolean;
  isPaid?: boolean;
  isFirstUser?: boolean;
}

export const baseWCSchemaShape = ({
  isOwner,
  isCompanyEmployee,
  isPaid,
  isFirstUser,
}: BaseWCSchemaArgs) =>
  Yup.object().shape({
    officeLocationId: Yup.string().when("isRemote", {
      is: "No",
      then: (schema) =>
        schema
          .required("You missed this one")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          ),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),

    isPaid: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
    title: Yup.string()
      .max(50, "Less than 50 characters, please.")
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
    roleDescription: Yup.string()
      .required("You missed this one")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
    isRemote: Yup.string().required("You missed this one"),

    ownerStake: isOwner
      ? Yup.string()
          .matches(numberRegex, "Numbers only, please.")
          .required("You missed this one")
          .test(
            "valid percentage",
            "Please enter a value between 0 - 100",
            (value) => {
              const val = parseInt(value || "0", 10);
              return 0 <= val && val <= 100;
            }
          )
      : Yup.string().notRequired().nullable(),

    email: !isFirstUser
      ? Yup.string()
          .matches(emailRegex, "Please enter a valid email address.")
          .required("You missed this one")
      : Yup.string().notRequired().nullable(),

    address1: Yup.string()
      .matches(
        addressRegex,
        "No PO Boxes, business mailing addresses, or virtual mailboxes."
      )
      .when("isRemote", {
        is: "Yes",
        then: Yup.string()
          .required("You missed this one")
          .max(50, "Less than 50 characters, please.")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          ),
        otherwise: Yup.string().notRequired(),
      }),
    address2: Yup.string()
      .optional()
      .max(50, "Less than 50 characters, please.")
      .test(
        "no emoji",
        "Sorry, no emoji 😔",
        (value) => !noEmojiRegex.test(value || "")
      ),
    city: Yup.string().when("isRemote", {
      is: "Yes",
      then: Yup.string()
        .required("You missed this one")
        .max(50, "Less than 50 characters, please.")
        .test(
          "no emoji",
          "Sorry, no emoji 😔",
          (value) => !noEmojiRegex.test(value || "")
        ),
      otherwise: Yup.string().notRequired(),
    }),
    state: Yup.string().when("isRemote", {
      is: "Yes",
      then: Yup.string()
        .required("You missed this one")
        .test(
          "no emoji",
          "Sorry, no emoji 😔",
          (value) => !noEmojiRegex.test(value || "")
        ),
      otherwise: Yup.string().notRequired(),
    }),
    zip: Yup.string()
      .matches(
        postalcodeRegex,
        "Zip code should be 5 digits or 5+4 digits. For example, 10041 or 10041-0004."
      )
      .when("isRemote", {
        is: "Yes",
        then: Yup.string()
          .required("You missed this one")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          ),
        otherwise: Yup.string().notRequired(),
      }),

    employeeType: isCompanyEmployee
      ? Yup.string().required("You missed this one")
      : Yup.string().notRequired().nullable(),

    hourlyRate: Yup.string().when("salaryType", {
      is: "Hourly rate",
      then: (schema) =>
        schema
          .required("You missed this one")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          )
          .test(
            "greater than 0",
            "Must be greater than 0.",
            (value) => parseFloat(value || "") > 0
          )
          .test("invalid wage format", "Please enter a valid wage", wageCheck),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
    hoursWeekly: Yup.string().when("salaryType", {
      is: "Hourly rate",
      then: (schema) =>
        schema
          .required("You missed this one")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          )
          .test(
            "min hours",
            "The maximum number of hours is 168.",
            (value) => parseInt(value || "") <= 168
          )
          .test(
            "min hours",
            "The minimum number of hours is 1.",
            (value) => parseInt(value || "") >= 1
          ),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),

    salaryType: isPaid
      ? Yup.string().required("You missed this one")
      : Yup.string().notRequired().nullable(),

    salary: Yup.string().when("salaryType", {
      is: "Annual salary",
      then: (schema) =>
        schema
          .required("You missed this one")
          .test(
            "no emoji",
            "Sorry, no emoji 😔",
            (value) => !noEmojiRegex.test(value || "")
          )
          .test(
            "greater than 0",
            "Must be greater than 0.",
            (value) => parseFloat(value || "") > 0
          )
          .test("invalid wage format", "Please enter a valid wage", wageCheck),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),

    exemptionStatus: isPaid
      ? Yup.string().required("You missed this one")
      : Yup.string().notRequired().nullable(),
  });
